export type ExperimentId = "titration" | "electrical";

export type TitrationTrial = {
  titreMl: number;
  effectiveBaseMl: number;
  overshootMl: number;
  airBubblePresent: boolean;
  eyeLevel: boolean;
};

export const TITRATION_CONFIG = {
  acidVolumeMl: 25,
  acidConcentrationM: 0.08,
  baseConcentrationM: 0.1,
  initialBuretteReadingMl: 0.2,
  tipDeadVolumeMl: 0.25,
  equivalenceVolumeMl: 20,
} as const;

export function effectiveBaseVolumeMl(
  dispensedMl: number,
  airBubblePresent: boolean
) {
  return Math.max(
    0,
    dispensedMl - (airBubblePresent ? TITRATION_CONFIG.tipDeadVolumeMl : 0)
  );
}

export function titrationPh(dispensedMl: number, airBubblePresent: boolean) {
  const baseMl = effectiveBaseVolumeMl(dispensedMl, airBubblePresent);
  const acidMoles =
    TITRATION_CONFIG.acidConcentrationM *
    (TITRATION_CONFIG.acidVolumeMl / 1000);
  const baseMoles = TITRATION_CONFIG.baseConcentrationM * (baseMl / 1000);
  const totalVolumeL = (TITRATION_CONFIG.acidVolumeMl + baseMl) / 1000;
  const difference = acidMoles - baseMoles;

  if (Math.abs(difference) < 1e-10) return 7;

  if (difference > 0) {
    const hydrogenM = difference / totalVolumeL;
    return -Math.log10(hydrogenM);
  }

  const hydroxideM = -difference / totalVolumeL;
  const pOh = -Math.log10(hydroxideM);
  return 14 - pOh;
}

export function phenolphthaleinAppearance(ph: number) {
  if (ph < 8.2) {
    return {
      label: "colourless",
      css: "rgba(248, 249, 247, 0.22)",
      persistentEndpoint: false,
    };
  }

  const strength = Math.min(1, Math.max(0.12, (ph - 8.2) / 1.8));
  return {
    label: strength < 0.45 ? "faint pink" : "pink",
    css: `rgba(236, 72, 153, ${0.16 + strength * 0.58})`,
    persistentEndpoint: ph >= 8.2,
  };
}

export function buretteReadingMl(dispensedMl: number, eyeLevel: boolean) {
  const parallaxOffsetMl = eyeLevel ? 0 : 0.08;
  return (
    TITRATION_CONFIG.initialBuretteReadingMl + dispensedMl + parallaxOffsetMl
  );
}

export function makeTitrationTrial(
  dispensedMl: number,
  airBubblePresent: boolean,
  eyeLevel: boolean
): TitrationTrial {
  const finalReading = buretteReadingMl(dispensedMl, eyeLevel);
  const titreMl = finalReading - TITRATION_CONFIG.initialBuretteReadingMl;
  const effectiveBaseMl = effectiveBaseVolumeMl(dispensedMl, airBubblePresent);

  return {
    titreMl,
    effectiveBaseMl,
    overshootMl: Math.max(
      0,
      effectiveBaseMl - TITRATION_CONFIG.equivalenceVolumeMl
    ),
    airBubblePresent,
    eyeLevel,
  };
}
export function selectConcordantTitres(trials: TitrationTrial[]) {
  if (trials.length < 2) return null;

  let best: {
    indexes: [number, number];
    titres: [number, number];
    differenceMl: number;
    meanMl: number;
  } | null = null;

  for (let first = 0; first < trials.length; first += 1) {
    for (let second = first + 1; second < trials.length; second += 1) {
      const titres: [number, number] = [
        trials[first].titreMl,
        trials[second].titreMl,
      ];
      const differenceMl = Math.abs(titres[0] - titres[1]);
      if (!best || differenceMl < best.differenceMl) {
        best = {
          indexes: [first, second],
          titres,
          differenceMl,
          meanMl: (titres[0] + titres[1]) / 2,
        };
      }
    }
  }

  if (!best || best.differenceMl > 0.1) return null;
  return best;
}

// Local guided-simulation tolerance, not an examination concordance rule.
export const GUIDED_ENDPOINT_OVERSHOOT_LIMIT_ML = 0.1;

export function guidedTitrationTrialIssue(
  trial: TitrationTrial
): string | null {
  if (trial.airBubblePresent) {
    return "The burette tip contained an air bubble. Keep this reading, then repeat with the bubble cleared.";
  }
  if (!trial.eyeLevel) {
    return "The burette was not read at eye level. Keep this reading, then repeat with your eye level with the meniscus.";
  }
  if (
    !Number.isFinite(trial.effectiveBaseMl) ||
    !Number.isFinite(trial.titreMl) ||
    !Number.isFinite(trial.overshootMl) ||
    trial.effectiveBaseMl < TITRATION_CONFIG.equivalenceVolumeMl
  ) {
    return "The endpoint was not reached. Keep this reading, then repeat and check the colour after swirling.";
  }
  if (trial.overshootMl > GUIDED_ENDPOINT_OVERSHOOT_LIMIT_ML + 1e-9) {
    return "Endpoint passed. Matching titres can still be inaccurate. Keep this reading and repeat, adding smaller amounts near the colour change.";
  }
  return null;
}

export function selectGuidedCalculationTitres(trials: TitrationTrial[]) {
  const eligible = trials
    .map((trial, index) => ({ trial, index }))
    .filter(({ trial }) => guidedTitrationTrialIssue(trial) === null);
  const pair = selectConcordantTitres(eligible.map(({ trial }) => trial));
  if (!pair) return null;
  return {
    ...pair,
    indexes: [
      eligible[pair.indexes[0]].index,
      eligible[pair.indexes[1]].index,
    ] as [number, number],
  };
}

export function acidConcentrationFromTitre(meanTitreMl: number) {
  return (
    (TITRATION_CONFIG.baseConcentrationM * meanTitreMl) /
    TITRATION_CONFIG.acidVolumeMl
  );
}

export type AmmeterPlacement = "series" | "parallel";
export type VoltmeterPlacement = "across-lamp" | "series";

export type ElectricalCircuitInput = {
  supplyVoltageV: number;
  seriesResistanceOhm: number;
  switchClosed: boolean;
  ammeterPlacement: AmmeterPlacement;
  voltmeterPlacement: VoltmeterPlacement;
};

export type ElectricalReading = {
  valid: boolean;
  error: string | null;
  currentA: number;
  lampVoltageV: number;
  lampResistanceOhm: number;
  lampPowerW: number;
  filamentTemperatureC: number;
};

const LAMP_COLD_RESISTANCE_OHM = 2.5;
const LAMP_HEATING_COEFFICIENT = 20;

function lampResistanceAtCurrent(currentA: number) {
  return (
    LAMP_COLD_RESISTANCE_OHM + LAMP_HEATING_COEFFICIENT * currentA * currentA
  );
}

export function solveFilamentCircuit(
  input: ElectricalCircuitInput
): ElectricalReading {
  if (input.ammeterPlacement === "parallel") {
    return {
      valid: false,
      error: "Ammeter overload: an ammeter must be connected in series.",
      currentA: 0,
      lampVoltageV: 0,
      lampResistanceOhm: LAMP_COLD_RESISTANCE_OHM,
      lampPowerW: 0,
      filamentTemperatureC: 20,
    };
  }

  if (input.voltmeterPlacement === "series") {
    return {
      valid: false,
      error:
        "Circuit is effectively open: the voltmeter should be across the lamp.",
      currentA: 0,
      lampVoltageV: 0,
      lampResistanceOhm: LAMP_COLD_RESISTANCE_OHM,
      lampPowerW: 0,
      filamentTemperatureC: 20,
    };
  }

  if (!input.switchClosed || input.supplyVoltageV <= 0) {
    return {
      valid: true,
      error: null,
      currentA: 0,
      lampVoltageV: 0,
      lampResistanceOhm: LAMP_COLD_RESISTANCE_OHM,
      lampPowerW: 0,
      filamentTemperatureC: 20,
    };
  }

  const supplyVoltage = input.supplyVoltageV;
  const seriesResistance = Math.max(0, input.seriesResistanceOhm);

  // Solve V = I(Rseries + Rlamp(I)) by bisection. This is deterministic,
  // stable and sufficient for the bounded current range of this practical.
  let low = 0;
  let high = Math.max(
    0.1,
    supplyVoltage / Math.max(0.1, LAMP_COLD_RESISTANCE_OHM)
  );

  for (let iteration = 0; iteration < 70; iteration += 1) {
    const current = (low + high) / 2;
    const lampResistance = lampResistanceAtCurrent(current);
    const predictedVoltage = current * (seriesResistance + lampResistance);

    if (predictedVoltage > supplyVoltage) high = current;
    else low = current;
  }

  const currentA = (low + high) / 2;
  const lampResistanceOhm = lampResistanceAtCurrent(currentA);
  const lampVoltageV = currentA * lampResistanceOhm;
  const lampPowerW = currentA * lampVoltageV;
  const filamentTemperatureC = 20 + 700 * lampPowerW;

  return {
    valid: true,
    error: null,
    currentA,
    lampVoltageV,
    lampResistanceOhm,
    lampPowerW,
    filamentTemperatureC,
  };
}

export function quantizeMeter(value: number, resolution: number) {
  return Math.round(value / resolution) * resolution;
}

export function meterDisplay(
  value: number,
  range: number,
  resolution: number,
  decimals: number
) {
  if (Math.abs(value) > range) return "OL";
  return quantizeMeter(value, resolution).toFixed(decimals);
}
